import { useEffect, useState } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { checkInviteCapabilities } from '@confluence/persistent-invite-button/entry-points/checkInviteCapabilities.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useIsOverTenEnabledUsers } from '@confluence/targeting/entry-points/useIsOverTenEnabledUsers.ts';
import {
	deserializeState,
	useGetOnboardingState,
	useSetOnboardingState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState.ts';
import { PERSISTENT_INVITE } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';

export const useFollowingFeedInviteDecision = ({
	enabled,
}: {
	enabled: boolean;
}): {
	inviteDecisionLoading: boolean;
	shouldShowInviteCta: boolean;
} => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { cloudId } = useSessionData();
	const { setOnboardingState, called } = useSetOnboardingState();
	// `undefined` means "not yet determined" — distinguishing it from a resolved `false` lets the
	// loading state cover the imperative permission fetch so the banner doesn't flash from the
	// caught-up state to the invite state once the fetch resolves.
	const [hasInvitePermissions, setInvitePermissions] = useState<boolean | undefined>(undefined);

	// We read the small-team signal from the SSR-safe `confluence_cloud_site_has_over_10_enabled_users`
	// TAP trait rather than the slow, region-limited `licenseConsumingUserCount` AGG field (which
	// would risk homepage SSR/VC90 regressions). Only an explicit `isOverTenEU === true` (>10 enabled
	// users) rules out the invite branch; a missing/errored/not-yet-resolved trait (`null`) is treated
	// as a small site, so we still run the capability check and let invite permissions be the gate.
	const { isOverTenEU, loading: traitLoading } = useIsOverTenEnabledUsers({
		skipBootstrap: !enabled,
	});
	const isSmallSite = isOverTenEU !== true;
	// Wait for the trait to resolve before running the capability check so we don't fire its BE
	// side-effect (onboarding-state cache write + analytics) for sites that turn out to have >10
	// enabled users.
	const skipInviteCapabilityCheck = !enabled || !isSmallSite || traitLoading;

	const { data: onboardingStateData, loading: onboardingStateLoading } = useGetOnboardingState(
		Object.values(PERSISTENT_INVITE),
		skipInviteCapabilityCheck,
	);

	const { hasInviteCapabilities } = deserializeState(onboardingStateData);

	useEffect(() => {
		const setInvitePermsEligibility = async () => {
			const invitePermission = await checkInviteCapabilities(
				cloudId,
				setOnboardingState,
				createAnalyticsEvent,
				'followingFeedEmptyState',
			);
			setInvitePermissions(invitePermission);
		};

		if (skipInviteCapabilityCheck || onboardingStateLoading) {
			return;
		}

		if (hasInviteCapabilities !== undefined) {
			setInvitePermissions(hasInviteCapabilities);
			return;
		}

		if (!called) {
			void setInvitePermsEligibility();
		}
	}, [
		called,
		cloudId,
		createAnalyticsEvent,
		hasInviteCapabilities,
		onboardingStateLoading,
		setOnboardingState,
		skipInviteCapabilityCheck,
	]);

	// On a small site we are still deciding until either the cached capability or the fetched
	// permission resolves; keep loading true across that window so the CTA skeleton stays up.
	const inviteCapabilityResolved =
		hasInviteCapabilities !== undefined || hasInvitePermissions !== undefined;

	return {
		inviteDecisionLoading:
			enabled &&
			(traitLoading || (isSmallSite && (onboardingStateLoading || !inviteCapabilityResolved))),
		shouldShowInviteCta:
			enabled && isSmallSite && Boolean(hasInviteCapabilities ?? hasInvitePermissions),
	};
};
